import Debug from 'debug';
import { type Client, type Subscription } from 'faye';
import { type Editor, type Selection } from 'slate';

const debug = Debug('slate-collaboration:selection-synchronizer');

/**
 * Information about this user which will be sent to other collaborators on
 * the document.
 */
export interface UserInfo {
  userId: string;
  displayName: string;
}

// State of a selection, as sent over the wire.
export interface SelectionState {
  docVersion: number;
  receivedAt?: number;
  selection: Selection;
  userId: string;
  selectionId: number;
}

const insecureRandomId = () => Math.floor(Math.random() * 10000000);

/**
 * Synchronize the state of the selections of all collaborators in the document
 * over a Faye websocket connection.
 */
class SelectionSynchronizer {
  fayeClient: Client;
  subscription: Subscription;
  selectionId: number;
  docVersion: number;
  userInfo: UserInfo;
  documentPath: string;
  onSelectionUpdate: (message: SelectionState, editor?: Editor) => void;

  selections: SelectionState[] = [];
  lastSentSelection: SelectionState;

  // Some object envy here. I wonder if I can come up with a better pattern.
  constructor({
    fayeClient,
    onSelectionUpdate,
    documentPath,
  }: {
    fayeClient: Client;
    onSelectionUpdate: (message: SelectionState, editor?: Editor) => void;
    documentPath: string;
  }) {
    this.fayeClient = fayeClient;
    this.selectionId = insecureRandomId();
    this.userInfo = {
      displayName: 'Anonymous user',
      userId: 'Unknown',
    };

    this.documentPath = documentPath;
    this.onSelectionUpdate = onSelectionUpdate;

    this.subscription = this.fayeClient.subscribe(
      `/selections/${documentPath}`,
      (message) => {
        if (message.selectionId === this.selectionId) return;
        if (message.docVersion < this.docVersion) return;

        message.receivedAt = Date.now();

        if (message.docVersion === this.docVersion) {
          this.onSelectionUpdate(message);
          if (debug.enabled) debug(`received ${JSON.stringify(message)}`);
        } else if (debug.enabled) {
          // We received a selection for a version of the document we haven't
          // received yet. We will queue the selection for when the document
          // arrives later.
          debug(`received and queued ${JSON.stringify(message)}`);
        }
        this.selections.push(message);
      }
    );
  }

  // Set the display name of the current user.
  setUserInfo({ userId, displayName }: UserInfo) {
    this.userInfo.userId = userId;
    this.userInfo.displayName = displayName;
    if (this.lastSentSelection) {
      this.lastSentSelection.userId = userId;
      // We don't sent displayName over the wire - it must be looked up by the
      // collaborator - so it can't be spoofed.
      this.resendSelection();
    }
  }

  didUpdateDocumentToVersion(newDocVersion) {
    if (newDocVersion === this.docVersion) return;

    this.docVersion = newDocVersion;
    if (debug.enabled) debug(`accepting version ${this.docVersion}`);

    this.selections = this.selections.filter(
      (message) => message.docVersion >= newDocVersion
    );

    this.selections
      .filter((message) => message.docVersion === newDocVersion)
      .forEach((message) => {
        if (debug.enabled) debug(`releasing ${JSON.stringify(message)}`);
        this.onSelectionUpdate(message);
      });
  }

  sendSelection(snapshot, docVersion?: number) {
    if (typeof docVersion !== 'number') return;
    if (!snapshot.selection) return;
    if (debug.enabled && this.docVersion !== docVersion) {
      debug(`sending & accepting version ${docVersion}`);
    }
    this.docVersion = docVersion;
    const selection: SelectionState = {
      selectionId: this.selectionId,
      userId: this.userInfo.userId,
      selection: snapshot.selection,
      docVersion,
    };
    this.lastSentSelection = selection;
    this._publishSelection(selection);
  }

  refreshSelections(docVersion) {
    this.selections
      .filter((message) => message.docVersion === docVersion)
      .forEach((message) => this.onSelectionUpdate(message));
  }

  transformSelection(selectionData, transformFunction) {
    return {
      ...selectionData,
      selection: transformFunction(selectionData.selection),
    };
  }

  resendSelection() {
    if (this.lastSentSelection) this._publishSelection(this.lastSentSelection);
  }

  stopSynchronizing() {
    this.subscription.cancel();
  }

  _publishSelection(selection) {
    if (debug.enabled) debug(`publishing ${JSON.stringify(selection)}`);
    this.fayeClient.publish(`/selections/${this.documentPath}`, selection);
  }
}

export default SelectionSynchronizer;
